import { useEffect, useState, useCallback } from 'react';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';

// T365 — the openings sitting inside the job digests we already store.
//
// T359 filed alerts away so they would stop cluttering application threads.
// That was right, and it left them doing nothing — while each is a list of
// openings at a company he has already engaged with. On his mailbox: 79
// postings he has never seen.
//
// Listing them is free: the title and the URL are both in the message already.
// Fetching a full description is not, so nothing is imported until he picks it.
// The cost is stated before the click, not discovered after it.

// Measured from the real ledger: `import_extract` has averaged $0.026 a job
// across 65 runs. Shown so the button is a decision rather than a surprise.
const COST_PER_JOB = 0.026;

function fmt(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

export default function AlertJobs() {
  const [jobs, setJobs] = useState(null);
  const [known, setKnown] = useState(0);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/emails/alert-jobs');
      setJobs(r.jobs ?? []);
      setKnown(r.known ?? 0);
    } catch {
      setJobs([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (url) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });

  const importPicked = async () => {
    const urls = [...picked];
    if (!urls.length) return;
    setBusy(true);
    try {
      await api.post('/jobs/import', { urls });
      // They leave this list as soon as they exist as jobs; until the import
      // finishes they would just sit here looking un-actioned.
      setJobs((prev) => (prev ?? []).filter((j) => !picked.has(j.url)));
      setPicked(new Set());
      toast(`Importing ${urls.length} job${urls.length === 1 ? '' : 's'} — they will appear on the dashboard.`, {
        kind: 'success',
      });
    } catch (e) {
      toast(e.body?.message || 'Could not start the import.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (!jobs || jobs.length === 0) return null;

  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left"
      >
        <span aria-hidden="true" className="text-slate-500">{open ? '▾' : '▸'}</span>
        <h2 className="text-sm font-medium text-brand-500">Jobs found in your alerts</h2>
        <span className="rounded-full bg-brand-600/20 px-2 py-0.5 text-[11px] text-brand-400">{jobs.length}</span>
      </button>

      {!open && (
        <p className="pl-6 text-xs text-slate-500">
          {jobs.length} openings the digests mentioned and the dashboard has never seen
          {known > 0 ? `, plus ${known} already there` : ''}.
        </p>
      )}

      {open && (
        <>
          <p className="max-w-prose pl-6 text-xs text-slate-500">
            Read out of the stored digests at no cost — the title and the link were already in the
            message. Importing fetches the full description so the job can be scored, which costs about{' '}
            <span className="tabular-nums">${COST_PER_JOB.toFixed(3)}</span> each.
          </p>

          <div className="flex flex-wrap items-center gap-3 pl-6">
            <button
              type="button"
              disabled={busy || picked.size === 0}
              onClick={importPicked}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
            >
              {busy ? 'starting…' : `Import ${picked.size || ''} selected`}
            </button>
            {picked.size > 0 && (
              <span className="text-xs text-slate-500 tabular-nums">
                ≈ ${(picked.size * COST_PER_JOB).toFixed(2)}
              </span>
            )}
            {picked.size > 0 && (
              <button
                type="button"
                onClick={() => setPicked(new Set())}
                className="text-xs text-slate-500 underline decoration-dotted hover:text-slate-300"
              >
                clear
              </button>
            )}
          </div>

          <ul className="space-y-1 pl-6">
            {jobs.map((j) => (
              <li key={j.url}>
                <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition hover:bg-slate-800/60">
                  <input
                    type="checkbox"
                    checked={picked.has(j.url)}
                    onChange={() => toggle(j.url)}
                    className="mt-0.5 shrink-0"
                    aria-label={`Select ${j.title}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs text-slate-200">{j.title}</span>
                    <span className="block truncate text-[11px] text-slate-500">{hostOf(j.url)}</span>
                  </span>
                  <span className="shrink-0 text-[11px] tabular-nums text-slate-500">{fmt(j.seenAt)}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
